import { Box, Button, IconButton, useTheme } from "@mui/material";
import React, { useState } from "react";
import { ImageSquare } from "@phosphor-icons/react";
import DashboardHeaderImage from "../../../assets/images/DashboardHeader.png";
import PlusIcon from "../../../assets/icons/Plus.png";
import { useAuthStore } from "../../../store/storeAuth";
import { UserService } from "../../../service/UserService";
import { useNotificationStore } from "../../../store/storeNotifications";
import { getAPIErrorMessage } from "../../../helpers/helpers";
import SearchMenu from "../../Dashboard/SearchMenu";
import TemplateSystemModal from "../../../components/TemplateSystemModal/TemplateSystemModal";
import CreateFlow from "../../Dashboard/CreateFlow";
import { useImageUpload } from "../../../hooks/useImageUpload";
import { useDarkMode } from "../../../hooks/useDarkMode";

const DashboardHeader: React.FC = () => {
  const { user: authUser, setAuthUser } = useAuthStore((state) => state);
  const { setNotification } = useNotificationStore((state) => state);
  const theme = useTheme();
  const { semanticColors, customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCreateFlowModal, setShowCreateFlowModal] = useState(false);

  const handleDashboardBackgroundImageSelected = async (
    url: string,
    source: any,
  ) => {
    try {
      await UserService.updateUser({
        cover: url,
      });
      setAuthUser({
        ...authUser!,
        cover: url,
      });
      setNotification({
        type: "success",
        message: "Background image updated successfully",
      });
    } catch (error) {
      setNotification({
        type: "error",
        message: getAPIErrorMessage(error as any),
      });
    }
  };

  const dashboardBackgroundUpload = useImageUpload({
    onImageSelected: handleDashboardBackgroundImageSelected,
  });

  const openTemplatesModal = () => {
    setShowTemplates(true);
  };

  return (
    <>
      {showTemplates && (
        <TemplateSystemModal onClose={() => setShowTemplates(false)} />
      )}

      {showCreateFlowModal && (
        <CreateFlow onHide={() => setShowCreateFlowModal(false)} />
      )}

      <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
        <img
          src={authUser?.cover || DashboardHeaderImage}
          alt="Header"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "24px",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 12,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              background: semanticColors.buttonBackground,
              color: semanticColors.buttonText,
              borderColor: semanticColors.tabActive,
              "&:hover": {
                background: semanticColors.buttonBackgroundHover,
                color: semanticColors.buttonText,
                borderColor: semanticColors.tabActive,
                boxShadow: "none",
              },
            }}
            onClick={openTemplatesModal}
          >
            Templates
          </Button>
          <Button
            onClick={() => setShowCreateFlowModal(true)}
            variant="contained"
            endIcon={<img src={PlusIcon} alt="Plus" />}
            sx={{
              flexShrink: 0,
              backgroundColor: "rgba(54, 80, 222, 1)",
              "&:hover": {
                backgroundColor: "rgba(54, 80, 222, 0.9)",
              },
            }}
          >
            New Agent
          </Button>
        </Box>

        <IconButton
          onClick={dashboardBackgroundUpload.openModal}
          sx={{
            position: "absolute",
            bottom: "16px",
            right: "16px",
            width: "40px",
            height: "40px",
            backgroundColor: semanticColors.buttonBackground,
            boxShadow: `2px 4px 35px 0px rgba(0, 0, 0, 0.25)`,
            "&:hover": {
              backgroundColor: semanticColors.buttonBackgroundHover,
            },
          }}
          title="Upload background image"
        >
          <ImageSquare
            size={20}
            color={isDarkMode ? palette.gray.A100 : palette.gray[700]}
          />
        </IconButton>
      </Box>
      {dashboardBackgroundUpload.ImageUploadModal}
    </>
  );
};

export default DashboardHeader;
